import { atom } from 'infrastructure/state/jotai.wrapper'
import { partnerIdAtom } from '../../components/ingestion/atoms'
import { atomWithRefresh } from 'infrastructure/state/atomWithRefresh'
import {
  IngestionConfig,
  IngestionContext,
  IngestionSourceOption,
  NormalizedIngestionSource,
  PartnerIngestionConfig,
} from './types'
import { useConfigurationFromSource } from './useConfigurationFromSource'
import { ICourseNode } from '../../components/ingestion/IngestedTree'
import { attributeIngestion } from './attributeIngestion'
import {
  LeafBodyValues,
  LeafTypes,
  NodeBodyKeys,
  NodeType,
} from 'shared/types/ingestion'
import { odellIngestionConfigAtom } from './Odell/atoms'
import { getFolderFiles } from '../../components/ingestion/Utility'

export const userMessageAtom = atom<string>('')

export const isBusyAtom = atom(false)

export const configFileAtom = atomWithRefresh<Promise<IngestionConfig>>(
  async (get) => {
    const partnerId = get(partnerIdAtom)
    if (!partnerId) return
    const fileUrl = `https://kiddom-ingestion.s3.amazonaws.com/partners/${partnerId}/export/ingestionConfig.json`
    const response = await fetch(fileUrl)
    return response.json()
  },
)

export const editedConfigAtom = atom<IngestionConfig>()

export const sourceDataAtom = atom<NormalizedIngestionSource>()

export const configurationFromSourceAtom = atom((get) => {
  const sourceData = get(sourceDataAtom)
  const configData = get(editedConfigAtom)
  if (!sourceData || !configData) return
  return useConfigurationFromSource(sourceData, configData)
})

export type IngestionTypeErrors = {
  unrecognized?: boolean
  noDestinationType?: boolean
  attributes?: Record<
    string,
    {
      unrecognized?: boolean
      noDestinationType?: boolean
    }
  >
  missing?: string[]
}

export type IngestionErrors = Record<string, IngestionTypeErrors>

export const ingestionErrorsAtom = atom<IngestionErrors>({})
export const ingestedTreeAtom = atom<ICourseNode>()

export const partnerIngestionConfigAtom = atom<PartnerIngestionConfig>(
  (get) => {
    const defaultConfig: PartnerIngestionConfig = {
      dataForNode() {
        return {}
      },
      attributeIngestion: attributeIngestion,
      filterSourceFiles(f) {
        return []
      },
      initialTransform() {
        return Promise.resolve(null)
      },
      stringContentPostProcessing: (s: string) => s,
    }

    const partnerId = get(partnerIdAtom)
    if (partnerId === 'Odell') {
      return get(odellIngestionConfigAtom)
    }
    return defaultConfig
  },
)

const partnerFilesAtom = atom(async (get) => {
  const partnerId = get(partnerIdAtom)
  if (!partnerId) return {}

  return getFolderFiles({
    bucket: 'kiddom-ingestion.s3.us-east-2.amazonaws.com',
    prefix: `partners/${partnerId}/export/`,
  })
})

export const selectedSourceOptionAtom = atom<IngestionSourceOption>()

export const filteredSourceFilesAtom = atom<Promise<IngestionSourceOption[]>>(
  async (get) => {
    const allSource = await get(partnerFilesAtom)
    const { filterSourceFiles } = get(partnerIngestionConfigAtom)
    return filterSourceFiles(allSource)
  },
)

export const ingestionContextAtom = atom<Promise<IngestionContext | undefined>>(
  async (get) => {
    const config = await get(editedConfigAtom)
    const ingestionErrors = get(ingestionErrorsAtom)
    const {
      dataForNode,
      attributeIngestion,
      stringContentPostProcessing = (s: string) => s,
    } = get(partnerIngestionConfigAtom)

    if (!config) return

    return {
      config,
      ingestionErrors,
      dataForNode: wrapDataForNode(dataForNode, ingestionErrors),
      attributeIngestion,
      stringContentPostProcessing,
    }
  },
)

export function wrapDataForNode(
  dataForNode: (
    t: NodeType,
    d: NormalizedIngestionSource,
  ) => Record<string, unknown>,
  ingestionErrors: IngestionErrors,
) {
  return (t: NodeType, d: NormalizedIngestionSource) => {
    const nodeData = dataForNode(t, d)
    switch (t) {
      case NodeType.Course: {
        const missing: string[] = [
          LeafBodyValues.GradeID,
          LeafTypes.Attribution,
          LeafBodyValues.SubjectID,
        ].filter((t) => !nodeData[t])
        if (missing.length) {
          ingestionErrors.course = { missing }
        }
        return nodeData
      }
      case NodeType.Activity: {
        return {
          [NodeBodyKeys.Assignable]: true,
          [LeafTypes.Points]: 4,
          [LeafTypes.AssessmentGrading]: 'formative',
          [LeafTypes.Assignment]: 'participation',
          ...nodeData,
        }
      }
    }
    return nodeData
  }
}
